import { useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import {
  Table,
  TableBody,
  TableFooter,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src/ui/table';

import { PlaqueMobilityBoneLossRow } from './PlaqueMobilityBoneLossRow';
import { ToothNumberRow } from './ToothNumberRow';
import { ToothStatusRow } from './ToothStatusRow';
import { ToothSurfaceMeasurements } from './ToothSurfaceMeasurements';
import { PerioChartModeContext, type PerioChartResponse } from './types';

const upperToothIds = Array.from({ length: 16 }, (_, index) =>
  String(index + 1),
);
const lowerToothIds = Array.from({ length: 16 }, (_, index) =>
  String(32 - index),
);

interface Props {
  mode: 'light' | 'dark';
  perioChartData: PerioChartResponse;
}

const EmptyRow = () => (
  <TableRow className="vai-h-5 vai-border-none"></TableRow>
);

export const PerioChart = ({ mode = 'dark', perioChartData }: Props) => {
  const { t } = useLingui();

  // Since we may not receive data for all teeth, we should create fixed
  // arrays
  const upperTeeth = useMemo(() => {
    return upperToothIds.map((toothId) => {
      const tooth = perioChartData.teeth.find(
        (th) => th.toothNumber === toothId,
      );
      return tooth ?? { toothNumber: toothId };
    });
  }, [perioChartData.teeth]);
  const lowerTeeth = useMemo(() => {
    return lowerToothIds.map((toothId) => {
      const tooth = perioChartData.teeth.find(
        (th) => th.toothNumber === toothId,
      );
      return tooth ?? { toothNumber: toothId };
    });
  }, [perioChartData.teeth]);

  return (
    <PerioChartModeContext.Provider value={mode}>
      <Table className="!vai-w-fit" mode={mode}>
        <TableHeader>
          <ToothNumberRow
            arch="upper"
            examDate={perioChartData.examDate}
            toothIds={upperToothIds}
          />
        </TableHeader>

        <TableBody>
          <ToothSurfaceMeasurements
            arch="upper"
            side="facial"
            teeth={upperTeeth}
          />
          <EmptyRow />
          <ToothSurfaceMeasurements
            arch="upper"
            side="lingual"
            teeth={upperTeeth}
          />
          <ToothStatusRow label={t`L`} teeth={upperTeeth} />
          <EmptyRow />
          <PlaqueMobilityBoneLossRow teeth={upperTeeth} />

          <PlaqueMobilityBoneLossRow teeth={lowerTeeth} />
          <EmptyRow />
          <ToothStatusRow label={t`L`} teeth={lowerTeeth} />
          <ToothSurfaceMeasurements
            arch="lower"
            side="lingual"
            teeth={lowerTeeth}
          />
          <EmptyRow />
          <ToothSurfaceMeasurements
            arch="lower"
            side="facial"
            teeth={lowerTeeth}
          />
        </TableBody>

        <TableFooter className="vai-bg-transparent">
          <ToothNumberRow arch="lower" toothIds={lowerToothIds} />
        </TableFooter>
      </Table>
    </PerioChartModeContext.Provider>
  );
};
